import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  Table,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from "reactstrap"
import moment from "moment"
import { FaChevronLeft, FaChevronRight, FaEye } from "react-icons/fa"
import { fetchData } from "utils/Common_api_functions"

const AuditLogs = () => {
  document.title = "Audit Logs | Milano Jewelry"

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])

  const [pageSize, setPageSize] = useState(10)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [viewModal, setViewModal] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)

  /* ================= SAMPLE DATA ================= */
  useEffect(() => {
    getAuditLogs()
  }, [])

  async function getAuditLogs() {
    const APIdata = await fetchData(setLoading, "logs")
    if (APIdata?.success) {
      setData(APIdata?.data?.reverse())
    }
  }

  /* ================= SEARCH ================= */

  const filteredData = data.filter(item =>
    [item.action, item.performedBy, item.details]
      .join(" ")
      .toLowerCase()
      .includes(searchString.toLowerCase())
  )

  /* ================= PAGINATION ================= */

  const totalPages = Math.ceil(filteredData.length / pageSize)

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  const handleView = log => {
    setSelectedLog(log)
    setViewModal(true)
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Settings" breadcrumbItem="Audit Logs" />

      <Card>
        <CardBody>
          {/* ===== FILTERS ===== */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-3">
              <select
                className="form-select rounded-3"
                style={{ width: "100px" }}
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <Input
                placeholder="Search..."
                className="rounded-3"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
          </div>

          {/* ===== TABLE ===== */}
          <Table striped responsive>
            <thead className="table-blue">
              <tr>
                <th>#</th>
                <th>Date & Time</th>
                <th>Action</th>
                <th>Performed By</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Records Found
                  </td>
                </tr>
              )}

              {paginatedData.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td>{moment(item.createdAt).format("MM-DD-YYYY hh:mm A")}</td>
                  <td>{item.action || "--"}</td>
                  <td>{item.staff?.name || "--"}</td>
                  <td>
                    <Button
                      className="btn-secondary"
                      onClick={() => handleView(item)}
                    >
                      <FaEye />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* ===== PAGINATION ===== */}
          <div className="d-flex justify-content-between">
            <span className="align-self-center">
              Page {currentPage} of {totalPages || 1}
            </span>
            {/* ===== PAGINATION ===== */}
            <div className="d-flex justify-content-center align-items-center gap-2 ">
              {/* Previous */}
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="btn-blue"
              >
                <FaChevronLeft />
              </Button>

              {/* Page Numbers (Show 5 at a time) */}
              {(() => {
                const pageNumbers = []
                const maxVisiblePages = 5

                let startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxVisiblePages / 2)
                )

                let endPage = startPage + maxVisiblePages - 1

                if (endPage > totalPages) {
                  endPage = totalPages
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <Button
                      key={i}
                      color={currentPage === i ? "primary" : "light"}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i}
                    </Button>
                  )
                }

                return pageNumbers
              })()}

              {/* Next */}
              <Button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="btn-blue"
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ===== VIEW MODAL ===== */}
      <Modal isOpen={viewModal} size="xl" centered>
        <ModalHeader toggle={() => setViewModal(false)}>
          Audit Log Details
        </ModalHeader>
        <ModalBody>
          {selectedLog && (
            <>
              <Table striped className="bg-light">
                <tbody>
                  <tr>
                    <td className="fw-bold">Date:</td>
                    <td>
                      {moment(selectedLog.dateTime).format(
                        "MM-DD-YYYY hh:mm A"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Action:</td>
                    <td> {selectedLog.action}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Performed By:</td>
                    <td> {selectedLog.staff?.name}</td>
                  </tr>
                  {selectedLog?.deletedData && (
                    <tr>
                      <th className="col-2">Deleted Data</th>
                      <td>
                        <div className="responsive-table">
                          <table border="1" className="table">
                            <tbody className="text-black">
                              {Object.entries(
                                JSON.parse(selectedLog?.deletedData)
                              ).map(([key, value]) => (
                                <tr key={key}>
                                  <th className="py-2 ">
                                    {key
                                      .replace(/([A-Z])/g, " $1")
                                      .replace(/^./, str => str.toUpperCase())}
                                  </th>
                                  <td className="py-2">
                                    {moment(value).format("MM-DD-YYYY | hh:mm A ") !==
                                      "Invalid date" &&
                                    value.length > 8 &&
                                    value.includes("-") ? (
                                      moment(value).format("MM-DD-YYYY | hh:mm A ")
                                    ) : (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: value || "--",
                                        }}
                                        style={{
                                          textWrap: "wrap",
                                          lineBreak: "anywhere",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}

                  {selectedLog?.updatedData && (
                    <tr>
                      <th className="col-2">Updated Data</th>
                      <td>
                        <div className="responsive-table">
                          <table border="1" className="table">
                            <tbody className="text-black">
                              {Object.entries(
                                JSON.parse(selectedLog?.updatedData)
                              ).map(([key, value]) => (
                                <tr key={key}>
                                  <th className="py-2 col-3">
                                    {key
                                      .replace(/([A-Z])/g, " $1")
                                      .replace(/^./, str => str.toUpperCase())}
                                  </th>
                                  <td className="py-2">
                                    {moment(value).format("MM-DD-YYYY | hh:mm A ") !==
                                      "Invalid date" &&
                                    value.length > 8 &&
                                    value.includes("-") ? (
                                      moment(value).format("MM-DD-YYYY | hh:mm A ")
                                    ) : (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: value || "--",
                                        }}
                                        style={{
                                          wordWrap: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}

                  {selectedLog?.previousData && (
                    <tr>
                      <th className="col-2">Previous Data</th>
                      <td>
                        <div className="responsive-table">
                          <table border="1" className="table">
                            <tbody className="text-black">
                              {Object.entries(
                                JSON.parse(selectedLog.previousData)
                              ).map(([key, value]) => (
                                <tr key={key}>
                                  <th className="py-2 col-3 ">
                                    {key
                                      .replace(/([A-Z])/g, " $1")
                                      .replace(/^./, str => str.toUpperCase())}
                                  </th>
                                  <td className="py-2">
                                    {moment(value).format("MM-DD-YYYY | hh:mm A ") !==
                                      "Invalid date" &&
                                    value.length > 8 &&
                                    value.includes("-") ? (
                                      moment(value).format("MM-DD-YYYY | hh:mm A ")
                                    ) : (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: value || "--",
                                        }}
                                        style={{
                                          wordWrap: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                  {selectedLog?.createdData && (
                    <tr>
                      <th className="col-2">Created Data</th>
                      <td>
                        <div className="table-responsive">
                          <table border="1" className="table">
                            <tbody className="text-black">
                              {Object.entries(
                                JSON.parse(selectedLog?.createdData)
                              ).map(([key, value]) => (
                                <tr key={key}>
                                  <th className="py-2 ">
                                    {key
                                      .replace(/([A-Z])/g, " $1")
                                      .replace(/^./, str => str.toUpperCase())}
                                  </th>
                                  <td className="py-2">
                                    {moment(value).format("MM-DD-YYYY | hh:mm A ") !==
                                      "Invalid date" &&
                                    value.length > 8 &&
                                    value.includes("-") ? (
                                      moment(value).format("MM-DD-YYYY | hh:mm A ")
                                    ) : (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: value || "--",
                                        }}
                                        style={{
                                          wordWrap: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                        }}
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* <tr>
                    <td className="fw-bold">Details:</td>
                    <td>{selectedLog.details}</td>
                  </tr> */}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default AuditLogs
